/**
 * Ask Sam (Google agent) + Live Chat (Hiver) dock - see snippets/support-dock.liquid.
 *
 * Ask Sam opens the Google & YouTube app's agent (<chat-messenger>, slideIn/slideOut).
 * Live Chat opens Hiver (a Chatwoot widget, window.$hiverChatWidget.toggle) and only shows
 * in the hours set under Theme settings > Chat, evaluated in Central time.
 *
 * The dock hides on scroll down and comes back on scroll up or once the page is still, and
 * stays hidden while a drawer, menu, chat window or the mobile keyboard is open.
 */
if (!customElements.get('support-dock')) {
  const TIME_ZONE = 'America/Chicago';

  /**
   * USAMM's yearly closures, worked out for any year so nobody has to update a list: New Year's
   * Eve and Day, MLK Day, Memorial Day, Juneteenth, Independence Day, Labor Day, Thanksgiving,
   * Christmas Eve and Day. A Saturday holiday is taken on the Friday before, a Sunday one on the
   * Monday after (two landing on the same day just close that day).
   * @param {number} year
   * @returns {string[]} YYYY-MM-DD dates
   */
  const usammHolidays = (year) => {
    const fmt = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    const date = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
    // nth weekday (0 = Sunday) of a month; n = -1 for the last one
    const nthWeekday = (y, m, weekday, n) => {
      if (n > 0) {
        const first = date(y, m, 1).getUTCDay();
        return date(y, m, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7);
      }
      const last = date(y, m + 1, 0);
      return date(y, m, last.getUTCDate() - ((last.getUTCDay() - weekday + 7) % 7));
    };
    const observed = (d) => {
      const day = d.getUTCDay();
      if (day === 6) return date(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate() - 1);
      if (day === 0) return date(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate() + 1);
      return d;
    };
    return [
      date(year, 1, 1), // New Year's Day
      nthWeekday(year, 1, 1, 3), // Martin Luther King Jr. Day
      nthWeekday(year, 5, 1, -1), // Memorial Day
      date(year, 6, 19), // Juneteenth
      date(year, 7, 4), // Independence Day
      nthWeekday(year, 9, 1, 1), // Labor Day
      nthWeekday(year, 11, 4, 4), // Thanksgiving
      date(year, 12, 24), // Christmas Eve
      date(year, 12, 25), // Christmas Day
      date(year, 12, 31), // New Year's Eve
      date(year + 1, 1, 1) // next New Year's Day, which can be observed on Dec 31
    ].map((d) => fmt(observed(d)));
  };
  const desktopMq = window.matchMedia('(min-width: 769px)');
  const hoverMq = window.matchMedia('(hover: hover)');
  const coarseMq = window.matchMedia('(any-pointer: coarse)');
  const TUCK_KEY = 'support-dock-tucked';
  // Travel in one direction needed before the dock hides or comes back, so drift doesn't flick it
  const SCROLL_TRAVEL = 24;
  // Always shown this close to the top; only hides once past the hero area
  const TOP_ZONE = 40;
  const HIDE_AFTER_DESKTOP = 120;
  const HIDE_AFTER_MOBILE = 80;
  // The dock rises above anything pinned to the bottom of the screen in its column: the
  // cookie banner, the sticky add to cart bar, promo strips... These are always checked too,
  // in case they don't take pointer events. Anything setting --support-dock-offset also works.
  const LIFT_SELECTOR = '.sticky-atc-panel';
  const LIFT_GAP = 12;
  // Bars that float a little above the bottom edge (the sticky add to cart has a 20px margin)
  // still count as pinned to the bottom
  const BOTTOM_TOLERANCE = 40;
  // Rising above something taller than this would leave the dock floating mid-page: hide instead
  const MAX_LIFT_SHARE = 0.7;
  // The chat windows the dock opens (it hides while they're open anyway)
  const LIFT_IGNORE = '#gecx-chat-widget-container, .woot-widget-holder, .woot--bubble-holder';
  // Never sit on the product page's own buy buttons, whatever Theme settings > Chat > Exclusion
  // elements says. (Not the add buttons on product cards: the dock would blink over collections.)
  const ALWAYS_AVOID = '.product-info__add-button, .product-info__add-button + .shopify-payment-button';
  const POLL_MS = 500;
  // The apps load after the page. Their buttons show straight away (no pop-in) and a tap before
  // an app is ready is held until it is. An app that still hasn't loaded after this long (blocked,
  // say) has its button removed.
  const APP_WAIT_MS = 15000;
  const HOURS_CHECK_MS = 60000;
  const TEXT_ENTRY = 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="range"]):not([type="color"]):not([type="file"]), textarea, select, [contenteditable="true"]';

  const getSam = () => {
    const messenger = document.querySelector('#gecx-chat-widget-container chat-messenger');
    return messenger && typeof messenger.slideIn === 'function' ? messenger : null;
  };
  // A chat window only counts as open if it's actually on screen. If an app says it's open but
  // shows nothing (a frame that failed to load, say), the dock mustn't stay hidden waiting for it.
  const isOnScreen = (el) => {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    return r.width > 0 && r.height > 0 && style.visibility !== 'hidden' && style.display !== 'none'
      && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
  };
  const isSamOpen = () => {
    const messenger = getSam();
    if (!messenger || messenger.classList.contains('messenger-hidden')) return false;
    const container = messenger.querySelector('chat-messenger-container');
    const panel = container && container.shadowRoot && container.shadowRoot.querySelector('.chat-wrapper');
    return isOnScreen(panel || container);
  };
  // Ready once its chat frame has loaded and checked in (hasLoaded), not just once the script ran:
  // opening it before then shows an empty full-screen layer on phones
  const getHiver = () => {
    const widget = window.$hiverChatWidget;
    return widget && typeof widget.toggle === 'function' && widget.hasLoaded !== false ? widget : null;
  };
  // Hiver's full-screen layer showing while its chat frame never appeared: an invisible layer over
  // the page that swallows every tap. Seen on iPhone Safari when the frame can't finish loading.
  const HIVER_STUCK_MS = 4000;
  const isHiverStuck = () => {
    const widget = getHiver();
    return !!widget && !!widget.isOpen
      && isOnScreen(document.querySelector('.woot-widget-holder'))
      && !isOnScreen(document.getElementById('chatwoot_live_chat_widget'));
  };
  // iPhone Safari: with its toolbar collapsed, a tap near the bottom edge only brings the toolbar
  // back, so the dock moves up clear of that area until the toolbar returns
  const ua = navigator.userAgent;
  const isIos = /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const isIosSafari = isIos && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  // iOS zooms the whole page in when a text box with text under 16px is tapped, which pushes the
  // chat window's close button off screen. The chat boxes are inside the apps' own frames, so
  // while a chat is open the page asks iOS not to auto-zoom (maximum-scale=1). iOS still allows
  // pinch zoom with this set; Android wouldn't, so it's iOS only.
  const viewportMeta = document.querySelector('meta[name="viewport"]');

  const isHiverOpen = () => {
    const widget = getHiver();
    if (!widget || !widget.isOpen) return false;
    // Hiver keeps its frame hidden until it has loaded
    return isOnScreen(document.querySelector('.woot-widget-holder'))
      && isOnScreen(document.getElementById('chatwoot_live_chat_widget'));
  };

  const readTucked = () => {
    try {
      return window.sessionStorage.getItem(TUCK_KEY) === '1';
    } catch (e) {
      return false;
    }
  };
  const writeTucked = (tucked) => {
    try {
      if (tucked) window.sessionStorage.setItem(TUCK_KEY, '1');
      else window.sessionStorage.removeItem(TUCK_KEY);
    } catch (e) {
      // Storage blocked: the tuck just won't carry over to the next page
    }
  };

  const track = (name) => {
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name });
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, {});
      }
    } catch (e) {
      // Tracking must never block opening the chat
    }
  };

  class SupportDock extends HTMLElement {
    connectedCallback() {
      this.inner = this.querySelector('.support-dock__inner');
      this.menu = this.querySelector('.support-dock__menu');
      this.samButton = this.querySelector('.js-support-sam');
      this.liveButton = this.querySelector('.js-support-live');
      this.toggleButton = this.querySelector('.js-support-toggle');
      this.tuckButton = this.querySelector('.js-support-tuck');
      this.tabButton = this.querySelector('.js-support-untuck');

      const delay = parseFloat(this.dataset.revealDelay);
      this.revealDelay = Number.isFinite(delay) ? delay * 1000 : 1200;
      this.exclusions = (this.dataset.exclusions || '').trim();
      this.lastScrollY = Math.max(0, window.scrollY);
      this.travel = 0;
      this.lift = 0;
      this.coveredFromBelow = false;
      this.away = false;
      this.inHours = this.checkHours();
      this.startedAt = Date.now();
      this.pending = null;
      // Installed = its code is on the page, even if it hasn't finished loading
      this.samInstalled = !!document.querySelector('#gecx-chat-widget-container');
      this.hiverInstalled = this.dataset.chatEnabled === 'true';

      this.classList.toggle('is-tucked', readTucked());

      this.handleScroll = this.handleScroll.bind(this);
      this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.update = this.update.bind(this);

      this.samButton.addEventListener('click', () => this.openSam());
      this.liveButton.addEventListener('click', () => this.openLiveChat());
      this.toggleButton.addEventListener('click', () => this.setMenuOpen(!this.classList.contains('is-open')));
      this.tuckButton.addEventListener('click', (evt) => this.setTucked(true, evt));
      this.tabButton.addEventListener('click', (evt) => this.setTucked(false, evt));
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      if (isIosSafari) {
        this.viewport = { width: window.innerWidth, minHeight: window.innerHeight };
        this.handleViewport = this.updateToolbarClearance.bind(this);
        window.addEventListener('resize', this.handleViewport, { passive: true });
        window.addEventListener('scroll', this.handleViewport, { passive: true });
      }
      document.addEventListener('pointerdown', this.handleDocumentPointerDown);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('focusin', this.update);
      document.addEventListener('focusout', this.update);

      // The apps' own launchers are hidden from here on (see support-dock.css)
      document.documentElement.classList.add('has-support-dock');

      this.update();
      this.classList.add('is-ready');
      this.pollTimer = setInterval(this.update, POLL_MS);
      this.hoursTimer = setInterval(() => {
        this.inHours = this.checkHours();
        this.update();
      }, HOURS_CHECK_MS);
    }

    disconnectedCallback() {
      clearInterval(this.pollTimer);
      clearInterval(this.hoursTimer);
      clearTimeout(this.revealTimer);
      window.removeEventListener('scroll', this.handleScroll);
      if (this.handleViewport) {
        window.removeEventListener('resize', this.handleViewport);
        window.removeEventListener('scroll', this.handleViewport);
      }
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('focusin', this.update);
      document.removeEventListener('focusout', this.update);
      document.documentElement.classList.remove('has-support-dock');
      this.setZoomLock(false);
    }

    /**
     * Whether Live Chat is staffed right now, by the schedule in theme settings (Central time)
     * @returns {boolean}
     */
    checkHours() {
      if (this.dataset.chatOffline === 'true') return false;

      try {
        const parts = {};
        new Intl.DateTimeFormat('en-US', {
          timeZone: TIME_ZONE,
          weekday: 'short',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          hourCycle: 'h23'
        }).formatToParts(new Date()).forEach((part) => {
          parts[part.type] = part.value;
        });

        const days = (this.dataset.days || '').split(',').map((day) => day.trim().slice(0, 3).toLowerCase());
        const closedDates = (this.dataset.closedDates || '').split(/[\s,]+/).filter(Boolean);
        const today = `${parts.year}-${parts.month}-${parts.day}`;
        const hour = parseInt(parts.hour, 10) % 24;
        const opens = parseInt(this.dataset.open, 10);
        const closes = parseInt(this.dataset.close, 10);

        const holiday = this.dataset.holidays !== 'false' && usammHolidays(Number(parts.year)).includes(today);

        return days.includes(parts.weekday.toLowerCase())
          && !holiday
          && !closedDates.includes(today)
          && hour >= opens
          && hour < closes;
      } catch (e) {
        // No reliable clock: don't offer Live Chat rather than offer it when nobody is there
        return false;
      }
    }

    /**
     * Refreshes which buttons show and whether the dock should be out of the way
     */
    update() {
      const stillLoading = Date.now() - this.startedAt < APP_WAIT_MS;
      const hasSam = !!getSam() || (this.samInstalled && stillLoading);
      const hasLive = this.hiverInstalled && this.inHours && (!!getHiver() || stillLoading);
      this.classList.toggle('has-sam', hasSam);
      this.classList.toggle('has-live', hasLive);

      // A tap made before its app had loaded
      if (this.pending === 'sam' && getSam()) this.openSam();
      else if (this.pending === 'live' && getHiver()) this.openLiveChat();
      else if (this.pending && !stillLoading) this.pending = null;

      this.clearStuckHiver();
      this.setZoomLock(isSamOpen() || isHiverOpen() || Date.now() < (this.openingUntil || 0));

      // Lift first: something covering most of the screen hides the dock instead
      this.updateLift();

      const blocked = this.coveredFromBelow || this.isBlocked();
      this.classList.toggle('is-blocked', blocked);
      if (blocked) this.setMenuOpen(false);

      // Hidden buttons shouldn't be reachable by Tab
      this.inner.inert = blocked || this.away;
    }

    /**
     * iOS: stop the page auto-zooming onto a chat window's text box while a chat is open
     * @param {boolean} lock
     */
    setZoomLock(lock) {
      if (!isIos || !viewportMeta || lock === !!this.zoomLocked) return;
      this.zoomLocked = lock;
      if (lock) {
        // Whatever the tag says now (other scripts add to it after load), restored exactly after
        this.savedViewport = viewportMeta.getAttribute('content') || '';
        viewportMeta.setAttribute('content', `${this.savedViewport}, maximum-scale=1`);
      } else if (this.savedViewport !== undefined) {
        viewportMeta.setAttribute('content', this.savedViewport);
      }
    }

    /**
     * Closes Hiver's layer if it has been open for a while with no chat showing in it, so the page
     * takes taps again
     */
    clearStuckHiver() {
      if (!isHiverStuck()) {
        this.hiverStuckSince = null;
        return;
      }
      if (!this.hiverStuckSince) this.hiverStuckSince = Date.now();
      if (Date.now() - this.hiverStuckSince > HIVER_STUCK_MS) {
        this.hiverStuckSince = null;
        getHiver().toggle();
      }
    }

    /**
     * iPhone Safari: how far the collapsed toolbar has grown the viewport, so the dock can sit
     * above the area where a tap only brings the toolbar back
     */
    updateToolbarClearance() {
      const { innerWidth, innerHeight } = window;
      // Rotated: start again
      if (innerWidth !== this.viewport.width) this.viewport = { width: innerWidth, minHeight: innerHeight };
      this.viewport.minHeight = Math.min(this.viewport.minHeight, innerHeight);

      const clearance = desktopMq.matches ? 0 : Math.round(innerHeight - this.viewport.minHeight);
      if (clearance !== this.toolbarClearance) {
        this.toolbarClearance = clearance;
        this.style.setProperty('--support-dock-toolbar', `${clearance}px`);
      }
    }

    /**
     * Whether something the dock mustn't cover (or compete with) is open
     * @returns {boolean}
     */
    isBlocked() {
      const { classList } = document.body;
      if (classList.contains('fixed') || classList.contains('overflow-hidden')
        || classList.contains('overlay-predictive-search')) return true;

      if (isSamOpen() || isHiverOpen()) return true;

      // Mobile keyboard: a text field is focused on a touch screen
      const active = document.activeElement;
      if (coarseMq.matches && active && !this.contains(active) && active.matches(TEXT_ENTRY)) return true;

      return this.overlapsExclusion();
    }

    /**
     * Whether the dock sits over anything in Theme settings > Chat > Dock: elements to avoid
     * @returns {boolean}
     */
    overlapsExclusion() {
      // With the Help menu opened on purpose, only the button's own footprint counts, or the open
      // card reaching over Add to Cart would close the menu straight away
      const rect = (this.classList.contains('is-open') ? this.toggleButton : this).getBoundingClientRect();
      if (!rect.width || !rect.height) return false;

      let elements;
      try {
        elements = document.querySelectorAll(this.exclusions ? `${this.exclusions}, ${ALWAYS_AVOID}` : ALWAYS_AVOID);
      } catch (e) {
        elements = document.querySelectorAll(ALWAYS_AVOID);
      }

      return Array.from(elements).some((el) => {
        if (this.contains(el)) return false;
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0
          && !(r.top > rect.bottom || r.right < rect.left || r.bottom < rect.top || r.left > rect.right);
      });
    }

    /**
     * Finds what's pinned to the bottom of the screen in the dock's column (a banner, a sticky
     * bar, a pop-up) by checking a few points along the bottom edge rather than the whole page
     * @param {DOMRect} rect - The dock's box
     * @returns {Set<Element>}
     */
    findBottomOverlays(rect) {
      const found = new Set();
      const pinnedAncestor = (el) => {
        for (let node = el; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
          if (this.contains(node) || node.matches(LIFT_IGNORE)) return null;
          // Fixed to the viewport, or sticky to the bottom edge. Not sticky page sections: those
          // stick to the top and can be taller than the screen.
          const { position, bottom } = getComputedStyle(node);
          if (position === 'fixed' || (position === 'sticky' && bottom !== 'auto')) return node;
        }
        return null;
      };

      [window.innerHeight - 2, window.innerHeight - BOTTOM_TOLERANCE + 8].forEach((y) => {
        [rect.left + 2, (rect.left + rect.right) / 2, rect.right - 2].forEach((x) => {
          document.elementsFromPoint(x, y).forEach((el) => {
            const pinned = pinnedAncestor(el);
            if (pinned) found.add(pinned);
          });
        });
      });
      document.querySelectorAll(LIFT_SELECTOR).forEach((el) => found.add(el));
      return found;
    }

    /**
     * Sits the dock 12px above anything pinned to the bottom of the screen in its column, and
     * lets it slide back down once that's gone
     */
    updateLift() {
      const rect = this.getBoundingClientRect();
      const restingBottom = rect.bottom + this.lift;
      let lift = 0;

      if (rect.width) {
        this.findBottomOverlays(rect).forEach((el) => {
          if (el.classList.contains('sticky-atc-panel--out')) return;
          const style = getComputedStyle(el);
          if (style.visibility !== 'visible' || style.display === 'none' || parseFloat(style.opacity) < 0.1) return;

          const r = el.getBoundingClientRect();
          const onScreen = r.width > 0 && r.height > 0 && r.top < window.innerHeight;
          // Anchored to the bottom edge, rather than a pinned element that happens to reach it
          const atBottom = r.bottom >= window.innerHeight - BOTTOM_TOLERANCE;
          const sharesColumn = !(r.right < rect.left || r.left > rect.right);
          if (!onScreen || !atBottom || !sharesColumn) return;

          lift = Math.max(lift, restingBottom - r.top + LIFT_GAP);
        });
      }

      lift = Math.max(0, Math.round(lift));
      this.coveredFromBelow = lift > window.innerHeight * MAX_LIFT_SHARE;
      if (this.coveredFromBelow) lift = this.lift;

      if (lift !== this.lift) {
        this.lift = lift;
        this.style.setProperty('--support-dock-lift', `${lift}px`);
      }
    }

    /**
     * Whether the shopper is using the dock, so it shouldn't slide away
     * @returns {boolean}
     */
    isInUse() {
      // Keyboard focus only: a tapped or clicked button keeps focus afterwards, and that
      // shouldn't stop the dock getting out of the way
      return this.classList.contains('is-open')
        || !!this.querySelector(':focus-visible')
        || (hoverMq.matches && this.matches(':hover'));
    }

    setAway(away) {
      if (this.away === away) return;
      this.away = away;
      this.classList.toggle('is-away', away);
      this.inner.inert = away || this.classList.contains('is-blocked');
    }

    /**
     * Hide on scroll down, show on scroll up, near the top, or once the page is still
     */
    handleScroll() {
      // Drawers and modals pin the page with body.fixed; its scroll position isn't the shopper's
      if (document.body.classList.contains('fixed')) return;

      const scrollY = Math.max(0, window.scrollY);
      const delta = scrollY - this.lastScrollY;
      this.lastScrollY = scrollY;
      if (!delta) return;

      this.travel = Math.sign(this.travel) === Math.sign(delta) ? this.travel + delta : delta;
      const hideAfter = desktopMq.matches ? HIDE_AFTER_DESKTOP : HIDE_AFTER_MOBILE;

      if (scrollY < TOP_ZONE) {
        this.setAway(false);
      } else if (this.travel > SCROLL_TRAVEL && scrollY > hideAfter) {
        if (!this.isInUse()) this.setAway(true);
      } else if (this.travel < -SCROLL_TRAVEL) {
        this.setAway(false);
      }

      // Every scroll event restarts the wait
      if (this.revealDelay) {
        clearTimeout(this.revealTimer);
        this.revealTimer = setTimeout(() => this.setAway(false), this.revealDelay);
      }
    }

    handleDocumentPointerDown(evt) {
      if (this.classList.contains('is-open') && !this.contains(evt.target)) this.setMenuOpen(false);
    }

    handleKeydown(evt) {
      if (evt.key === 'Escape' && this.classList.contains('is-open')) {
        this.setMenuOpen(false);
        this.toggleButton.focus();
      }
    }

    /**
     * Opens or closes the compact (product page) menu
     * @param {boolean} open
     */
    setMenuOpen(open) {
      if (this.classList.contains('is-open') === open) return;
      this.classList.toggle('is-open', open);
      this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    /**
     * Tucks the mobile chips into the edge tab, or brings them back
     * @param {boolean} tucked
     * @param {Event} [evt] - The click that did it
     */
    setTucked(tucked, evt) {
      this.classList.toggle('is-tucked', tucked);
      writeTucked(tucked);

      // The button pressed has just been hidden, so move keyboard focus to its replacement.
      // Only for keyboard presses (detail 0); after a tap, focus would show as the navy fill.
      if (evt && evt.detail === 0) {
        (tucked ? this.tabButton : this.samButton.offsetParent ? this.samButton : this.liveButton).focus();
      } else if (document.activeElement && this.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }

    openSam() {
      const sam = getSam();
      if (!sam) {
        this.pending = 'sam';
        return;
      }
      this.pending = null;

      const hiver = getHiver();
      if (hiver && hiver.isOpen) hiver.toggle();

      // Before the window shows, so the first tap in its text box doesn't zoom
      this.openingUntil = Date.now() + 3000;
      this.setZoomLock(true);
      sam.slideIn();
      track('ask_sam_open');
      this.setMenuOpen(false);
      this.update();
    }

    openLiveChat() {
      const hiver = getHiver();
      if (!hiver) {
        this.pending = 'live';
        return;
      }
      this.pending = null;

      const sam = getSam();
      if (sam && isSamOpen()) sam.slideOut();

      this.openingUntil = Date.now() + 3000;
      this.setZoomLock(true);

      // Hiver closes itself on clicks outside its window, which includes this one, so open it
      // once the click has finished
      setTimeout(() => {
        if (!hiver.isOpen) hiver.toggle();
        this.update();
      });
      track('live_chat_open');
      this.setMenuOpen(false);
      this.update();
    }
  }

  customElements.define('support-dock', SupportDock);
}
